
import WDElement from '../wd-element';
import WDButton from '../button/wd-button';
import WDUtil from '../../core/wd-util';
import WDModelItems from '../wd-model-items';
import WDNavbar from '../navbar/wd-navbar';
import { WDModelArray } from '../../core/wd-model';

export default class WDPillBox extends WDElement
{
    #prefix;
    #suffix;
    #content;
    #body;
    #loading;
    #navbar;
    #filters;
    #sortable;
    #config = {
        data: {
            model: [],
            initFetch: true,
            method: 'GET',
            resultKey: null,
            templates: {
                label: '${label}'
            }
        },
        pills: {},
        showLoading: false,
        loadingIcon: 'fa-solid fa-spinner',
        noResultsHandler: null,
        createItemHandler: null,
        clickHandler: null,
        allowSort: false,
        showNavbar: false
    };

    constructor(cfg)
    {
        super().classList.add('wd-pill-box');

        WDUtil.merge(this.#config, cfg);

        const dataCfg = this.#config.data;
        dataCfg.createItemHandler = (data) => this.createPillItem(data);
        dataCfg.noResultsHandler = () => this.createNoResultsElement()

        // Create the body using WDModelItems
        this.#body = new WDModelItems(dataCfg);

        this.#body.classList.add('wd-pill-box-body');

        // Set up the structure
        this.setupStructure();

        // Set up features
        if (this.#config.showNavbar) this.setupNavbar();
        if (this.#config.allowSort) this.setupSortable();

        // Forward events from the body
        const fwdEvents = ['model:fetch', 'model:fetched', 'items:added', 'model:set'];
        this.#body.on(fwdEvents, evt => this.trigger(evt.event, evt));

        return this;
    }

    setupStructure()
    {
        // Add the body to the content area
        this.content.append(this.#body);
    }

    cleanupCallback()
    {
        // Destroy sortable instance
        if (this.#sortable)
        {
            this.#sortable.destroy();
            this.#sortable = null;
        }

        // Clean up child components
        if (this.#body) {
            this.#body = null;
        }

        if (this.#navbar) {
            this.#navbar = null;
        }

        // Clear references
        this.#prefix = null;
        this.#suffix = null;
        this.#content = null;
        this.#loading = null;
        this.#filters = null;
        this.#config = null;

        super.cleanupCallback();
    }

    reset()
    {
        if (this.#prefix) this.#prefix.replaceChildren();
        if (this.#content) this.#content.replaceChildren();
        if (this.#suffix) this.#suffix.replaceChildren();
        
        // Re-add body after reset
        this.content.append(this.#body);
    }

    get prefix()
    {
        if (!this.#prefix)
        {
            this.#prefix = wdc('<div class="wd-pill-box-prefix"/>');
            this.prepend(this.#prefix);
        }

        return this.#prefix;
    }

    get suffix()
    {
        if (!this.#suffix)
        {
            this.#suffix = wdc('<div class="wd-pill-box-suffix"/>');
            this.append(this.#suffix);
        }

        return this.#suffix;
    }

    get content()
    {
        if (!this.#content)
        {
            this.#content = wdc('<div class="wd-pill-box-content"/>');
            this.append(this.#content);
        }

        return this.#content;
    }

    get navbar()
    {
        if (!this.#navbar)
        {
            this.#navbar = new WDNavbar();
            this.#navbar.classList.add('wd-pill-box-navbar');

            this.prepend(this.#navbar);
            this.classList.add('has-navbar');
        }

        return this.#navbar;
    }

    setupNavbar()
    {
        // Force creation of navbar
        this.navbar;
    }

    // Proxy model-related properties to the body
    get model() { return this.#body.model; }
    set model(data) { this.#body.model = data; }

    get fetch() { return this.#body.fetch; }
    set fetch(f) { this.#body.fetch = f; }

    get filters() { return this.#filters || null; }
    set filters(cfg)
    {
        if (!this.#filters)
        {
            // Can be passed a full filter config or just an array for more common usage
            const filterCfg = Array.isArray(cfg) ? {filters: cfg} : cfg;

            this.#filters = this.navbar.addFilters(filterCfg);
            this.#filters.on('filter:apply', () => this.fetchModel());
            this.#body.filters = this.#filters;
        }

        return this.#filters;
    }

    fetchModel()
    {
        return this.#body.fetchModel();
    }

    // Access to items
    get items() { return this.#body.items; }
    get itemArray() { return this.#body.itemArray; }
    get firstItem() { return this.#body.firstItem; }
    get lastItem() { return this.#body.lastItem; }
    itemAt(idx) { return this.items[idx] || null; }

    // Legacy API support
    addPill()
    {
        const pill = new WDPill();
        this.add(pill);
        return pill;
    }

    addBox()
    {
        const box = new WDPillBox();
        this.add(box);
        return box;
    }

    add(elem)
    {
        this.content.append(elem);
        return this;
    }

    loading(show)
    {
        if (!this.#loading)
        {
            this.#loading = wdc('<div class="wd-pills-loading"/>');
            const icon = wdc('<span class="fa-spin"/>');
            icon.classList.add(this.#config.loadingIcon);
            this.#loading.append(icon);

            this.append(this.#loading);
        }

        if (show === true) this.classList.add('is-loading');
        else this.classList.remove('is-loading');
    }

    createPillItem(data)
    {
        // Use custom handler if provided
        if (this.#config.createItemHandler)
        {
            return this.#config.createItemHandler(data);
        }

        // Default pill creation
        const pill = new WDPill(this.#config.pills);
        pill.model = data;
        pill.label = this.#config.data.templates.label.interpolate(data);

        // Add click handler if configured
        if (this.#config.clickHandler)
        {
            pill.on('click', () => this.#config.clickHandler(data, pill));
        }

        return pill;
    }

    createNoResultsElement()
    {
        if (this.#config.noResultsHandler)
        {
            return this.#config.noResultsHandler();
        }

        const noResults = wdc('<div class="wd-pill-box-no-results"/>');
        noResults.textContent = 'No results found';
        return noResults;
    }

    setupSortable()
    {
        import('sortable').then(module => {
            this.#sortable = module.Sortable.create(this.#body, {
                draggable: '.wd-pill',
                animation: 150,
                fallbackOnBody: true,
                swapThreshold: 0.65,
                onStart: (evt) => this.handleSortBegin(evt),
                onEnd: (evt) => this.handleSortEnd(evt),
            });
        });
    }

    handleSortBegin(evt)
    {
        this.trigger('items:sort', evt);
    }

    handleSortEnd(evt)
    {
        this.trigger('items:sorted', evt);
    }
}

export class WDPill extends WDButton
{
    #model;

    constructor(cfg)
    {
        super(cfg).classList.add('wd-pill');
    }

    set content(str)
    {
        this.label = str;
    }

    get content()
    {
        return this.label;
    }

    get box()
    {
        return this.closest('wd-pillbox');
    }

  set model(val)
    {
    this.#model = val;
    }

  get model()
    {
    return this.#model;
    }
}

customElements.define('wd-pillbox', WDPillBox);
customElements.define('wd-pill', WDPill);
